'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/frontier'
import * as DX from './exercises/deepen'

const DiffusionLab = dynamic(() => import('../_components/widgets/FrontierWidgets').then(m => m.DiffusionLab), { ssr: false })

export default function Diffusion() {
  return (
    <Lesson>
      <Step>
        <H>Generating by un-noising</H>
        <P>
          Language models generate one token at a time. Image generators mostly work completely differently, with an idea that
          sounds backwards: <strong>learn to remove noise</strong>.
        </P>
        <ol className="gd-list">
          <li>
            <strong>Forward process (no learning):</strong> take real data and add a little random noise, again and again, until
            nothing but noise is left.
          </li>
          <li>
            <strong>Train a network</strong> to look at a noisy example and predict the noise that was added.
          </li>
          <li>
            <strong>Generate:</strong> start from pure noise and repeatedly subtract the network’s noise prediction. Structure
            emerges, step by step.
          </li>
        </ol>
      </Step>

      <Step gate="q-diffusion">
        <H>Try it on a spiral</H>
        <P>
          Our “images” here are just points in 2D that form a spiral — the same idea, small enough to train in a few seconds.
          Slide the forward process to watch the spiral dissolve. Then train the denoiser (a small MLP) and generate brand-new
          points from pure noise.
        </P>
        <Figure n="10.6" wide caption="Left: the forward process destroys the data with noise. Right: after training, the network reverses it — starting from random points, step by step, the spiral reappears.">
          <DiffusionLab />
        </Figure>
        <Quiz
          id="q-diffusion"
          question="What exactly is the denoising network trained to predict?"
          options={[
            { label: 'The noise that was added to a clean example, given the noisy version and the time step', correct: true },
            { label: 'The final image directly from pure noise in one shot', why: 'That’s much harder; diffusion breaks it into many small, easier denoising steps.' },
            { label: 'Whether the image is real or fake', why: 'That’s a different family of models (GANs).' },
          ]}
          explain="Predicting the noise is a simple regression task with a mean-squared-error loss — exactly the kind of training you did in Module 6."
        />
      </Step>

      <Step gate="diffusion">
        <H>The forward process in code</H>
        <P>
          Each step mixes in a bit of noise with strength <M>{'\\beta_t'}</M>. Conveniently you can jump straight to any step{' '}
          <M>t</M> in one go, using <M>{'\\bar{\\alpha}_t'}</M>, the running product of <M>{'(1-\\beta)'}</M>:
        </P>
        <MB>{'\\mathbf{x}_t = \\sqrt{\\bar\\alpha_t}\\,\\mathbf{x}_0 + \\sqrt{1-\\bar\\alpha_t}\\,\\boldsymbol\\epsilon \\qquad \\boldsymbol\\epsilon \\sim \\mathcal{N}(0, I)'}</MB>
        <Exercise
          id="diffusion"
          title="Noise schedule and noising"
          {...EX.diffusion}
          hints={[
            <>Keep a running product starting at 1: multiply by <C>(1 - b)</C> each step and push it.</>,
            <>
              <C>{'x0.map((x, i) => Math.sqrt(abar) * x + Math.sqrt(1 - abar) * eps[i])'}</C>
            </>,
          ]}
        />
        <Callout kind="why">
          For real images the points are millions of pixels, the denoiser is a large network (often a transformer), and a text
          prompt steers each denoising step. But the recipe is the one you just ran.
        </Callout>
      </Step>

      <Step gate="denoise">
        <H>Running it backwards</H>
        <P>
          The network is trained to look at a noisy <M>{'\\mathbf{x}_t'}</M> and predict the noise <M>{'\\hat{\\boldsymbol\\epsilon}'}</M>{' '}
          that was added. Given that guess, rearranging the forward formula gives an estimate of the clean data. Generation then
          takes many small steps: estimate the clean data, re-noise it to a slightly lower noise level, and repeat until the
          noise is gone.
        </P>
        <Exercise
          id="denoise"
          title="predictX0 and step"
          {...DX.denoise}
          hints={[
            <>
              <C>{'(x - Math.sqrt(1 - abar) * epsHat[i]) / Math.sqrt(abar)'}</C>
            </>,
            <>
              <C>{'Math.sqrt(abarPrev) * x0hat[i] + Math.sqrt(1 - abarPrev) * epsHat[i]'}</C>
            </>,
          ]}
        />
      </Step>
    </Lesson>
  )
}
